import React, {
  useState,
  useRef,
  MouseEvent,
  ReactElement,
  ComponentType,
} from 'react';
import { Menu, MenuItem, Typography } from '@mui/material';

export interface MenuOption {
  label: string;
  /** Supports SVG components, MUI icons, or custom components */
  icon?: ComponentType<any>;
  onClick: () => void;
  color?: string;
  disabled?: boolean;
  divider?: boolean;
}

interface DropdownMenuProps {
  /** The element that opens the menu (e.g., <Button> or <IconButton>) */
  trigger: ReactElement;
  options: MenuOption[];
  /** Customizing positioning if needed */
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  trigger,
  options,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null); // ← ref owns the anchor position
  const open = Boolean(anchorEl);

  const handleOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(triggerRef.current); // ← use ref instead of e.currentTarget
  };

  const handleClose = (e?: MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  const handleItemClick = (onClick: () => void, e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onClick();
    handleClose();
  };

  const menuTrigger = React.cloneElement(trigger, {
    // ← onClick removed: span owns the click, MenuIcon's onClick becomes a no-op
    element: anchorEl, // ← still passed for MenuIcon's active styling
    id: trigger.props.id || `menu-trigger-${Math.random()}`,
  });

  return (
    <>
      <span
        ref={triggerRef}
        onClick={handleOpen}
        style={{ display: 'inline-flex' }} // ← inline-flex so the span has real dimensions for anchoring
      >
        {menuTrigger}
      </span>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: {
            className: 'min-w-[160px] py-1',
            sx: { borderRadius: '8px' },
          },
        }}
      >
        {options.map((option, index) => {
          const Icon = option.icon;
          return (
            <MenuItem
              key={`${option.label}-${index}`}
              disabled={option.disabled}
              divider={option.divider}
              onClick={(e) => handleItemClick(option.onClick, e)}
              className='!p-2 !px-4 flex gap-3 items-center'
            >
              {Icon && (
                <Icon
                  width={18}
                  height={18}
                  style={{ color: option.color || 'var(--muted)' }}
                />
              )}
              <Typography
                variant='body2'
                sx={{ color: option.color || 'var(--text-2)', fontWeight: 500 }}
              >
                {option.label}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
